import type { InteractionType } from '@azure/msal-browser';
import { useMsalStore } from '../stores/msal-store';
import {
    toValue,
    type MaybeRefOrGetter,
    computed,
    type ComputedRef,
} from 'vue';
import type { MaybeAccountRef } from '../utils/accountSelectors';

export interface ProfileEditOptions {
    interactionType?: InteractionType.Popup | InteractionType.Redirect;
    extraQueryParameters?: Record<string, string>;
    account?: MaybeAccountRef;
}

export interface UseProfileEditOptions {
    interactionType?: MaybeRefOrGetter<
        InteractionType.Popup | InteractionType.Redirect | null | undefined
    >;
    extraQueryParameters?: MaybeRefOrGetter<
        Record<string, string> | null | undefined
    >;
    account?: MaybeAccountRef;
}

export interface UseProfileEditResult {
    profileEdit: (overwriteOptions?: ProfileEditOptions) => Promise<void>;
    loading: ComputedRef<boolean>;
    disabled: ComputedRef<boolean>;
    available: ComputedRef<boolean>;
}

export function useProfileEdit(
    options: UseProfileEditOptions = {}
): UseProfileEditResult {
    const store = useMsalStore();

    const loading = computed(() => store.loginLoading);
    const available = computed(() => store.profileEditAvailable);
    const disabled = computed(() => !available.value || !store.idle);

    async function profileEdit(overwriteOptions: ProfileEditOptions = {}) {
        if (loading.value) {
            console.warn(
                'Tried to start profileEdit while another login-method was still loading.'
            );
            return;
        }

        if (disabled.value) {
            console.warn(
                'Tried to start profileEdit while msal was not (yet) idle.'
            );
            return;
        }

        await store.profileEdit({
            account: overwriteOptions.account ?? options.account,
            interactionType:
                overwriteOptions.interactionType ??
                toValue(options.interactionType) ??
                undefined,
            extraQueryParameters: {
                ...(toValue(options.extraQueryParameters) ?? {}),
                ...(overwriteOptions.extraQueryParameters ?? {}),
            },
        });
    }

    return { profileEdit, loading, disabled, available };
}
